import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    let mounted = true;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      
      console.log('[Callback] Starting authentication...');
      console.log('[Callback] API URL:', API);
      console.log('[Callback] Code present:', !!code);
      console.log('[Callback] Error param:', errorParam);
      
      if (errorParam) {
        console.log('[Callback] Authentication was cancelled');
        if (mounted) {
          setError('Authentication was cancelled or denied');
          setTimeout(() => navigate('/'), 3000);
        }
        return;
      }

      if (!code) {
        console.log('[Callback] No authorization code');
        if (mounted) {
          setError('No authorization code received');
          setTimeout(() => navigate('/'), 3000);
        }
        return;
      }

      try {
        if (mounted) setStatus('Exchanging authorization code...');
        
        console.log('[Callback] Calling backend callback endpoint...');
        const redirectUri = `${window.location.origin}/auth/callback`;
        const response = await axios.get(`${API}/auth/callback`, {
          params: { code, redirect_uri: redirectUri }
        });

        console.log('[Callback] Backend response received');
        console.log('[Callback] Full response:', JSON.stringify(response.data, null, 2));
        
        const { access_token, user } = response.data;
        
        if (!access_token || !user) {
          throw new Error('Invalid response from server');
        }
        
        console.log('[Callback] User:', user.display_name);
        console.log('[Callback] is_admin:', user.is_admin);
        console.log('[Callback] is_admin type:', typeof user.is_admin);
        
        if (mounted) setStatus('Setting up your session...');
        
        // Store the token and user data
        login(access_token, user);
        
        if (mounted) setStatus('Redirecting...');
        
        // Wait a moment for state to propagate
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Redirect based on admin status
        const destination = user.is_admin === true ? '/admin' : '/dashboard';
        console.log('[Callback] Redirecting to:', destination);
        
        if (mounted) {
          navigate(destination, { replace: true });
        }
        
      } catch (err) {
        console.error('[Callback] Authentication failed:', err);
        console.error('[Callback] Error details:', err.response?.data);
        
        if (mounted) {
          const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
          setError(errorMessage);
          setTimeout(() => navigate('/'), 3000);
        }
      }
    };

    handleCallback();

    return () => {
      mounted = false;
    };
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        {error ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-3xl">✕</span>
            </div>
            <h1 className="text-2xl font-bold text-red-500 mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              AUTHENTICATION FAILED
            </h1>
            <p className="text-slate-400 mb-4">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting to home...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 text-red-500 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4 text-red-500" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              AUTHENTICATING
            </h1>
            <p className="text-slate-400">{status}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
